"use client";

import { useEffect, useMemo, useState } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { Info, Loader2, Search, X } from "lucide-react";

import { readSkillError } from "@/components/command-skills/skill-api";
import { useShareCandidates, useSkillShares } from "@/hooks/use-command-skills";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getInitials } from "@/utils/global";
import type { SkillDetail, SkillShare } from "@/types/command-skill";
import { cn } from "@/lib/utils";

interface ShareSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: SkillDetail;
  onMissing: () => void;
  onPublishRequest: () => void;
  onUnpublishRequest: () => void;
}

function personName(fullname: string | null, email: string | null): string {
  return fullname?.trim() || email || "Member";
}

export function ShareSkillDialog({
  open,
  onOpenChange,
  skill,
  onMissing,
  onPublishRequest,
  onUnpublishRequest,
}: ShareSkillDialogProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, { wait: 300 });
  const [people, setPeople] = useState<SkillShare[]>(skill.shares);
  const [error, setError] = useState<string | null>(null);
  const shares = useSkillShares(skill.id);
  const trimmedQuery = debouncedQuery.trim();
  const candidates = useShareCandidates(trimmedQuery, open && trimmedQuery.length > 0);

  useEffect(() => {
    if (open) {
      setPeople(skill.shares);
      setQuery("");
      setError(null);
    }
  }, [open, skill.shares]);

  const selectedIds = useMemo(() => new Set(people.map((person) => person.user_id)), [people]);
  const results = (candidates.data?.items ?? []).filter((person) => !selectedIds.has(person.id));
  const canPublishToggle = skill.permissions.can_publish || skill.permissions.can_unpublish;

  const save = () => {
    setError(null);
    shares.mutate(
      people.map((person) => person.user_id),
      {
        onSuccess: () => onOpenChange(false),
        onError: (mutationError) => {
          const parsed = readSkillError(mutationError, "Could not update sharing");
          if (parsed.code === "COMMAND_TEAM__SKILL_NOT_FOUND") {
            onMissing();
            return;
          }
          setError(parsed.message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !shares.isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Share <span className="font-mono">{skill.name}</span>
          </DialogTitle>
          <DialogDescription>People in your organization</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Add people by name or email…"
              startIcon={<Search className="size-4" />}
            />
            {query.trim() ? (
              <div className="absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-xl border bg-popover p-1 shadow-lg">
                {query.trim() !== trimmedQuery || candidates.isFetching ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
                ) : results.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No matching people</p>
                ) : (
                  results.map((person) => (
                    <button
                      key={person.id}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-muted"
                      onClick={() => {
                        if (people.length >= 100) {
                          setError("You can share with up to 100 people.");
                          return;
                        }
                        setPeople((current) => [
                          ...current,
                          {
                            user_id: person.id,
                            fullname: person.fullname,
                            email: person.email,
                            profile_image_url: person.profile_image_url,
                          },
                        ]);
                        setQuery("");
                        setError(null);
                      }}
                    >
                      <PersonAvatar
                        name={personName(person.fullname, person.email)}
                        image={person.profile_image_url}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm">
                          {personName(person.fullname, person.email)}
                        </span>
                        {person.email ? (
                          <span className="block truncate text-xs text-muted-foreground">{person.email}</span>
                        ) : null}
                      </span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          <ul className="max-h-56 space-y-1 overflow-auto">
            {people.length === 0 ? (
              <li className="px-1 py-3 text-sm text-muted-foreground">Not shared with anyone yet.</li>
            ) : (
              people.map((person) => (
                <li key={person.user_id} className="flex items-center gap-2 rounded-lg px-1 py-1">
                  <PersonAvatar
                    name={personName(person.fullname, person.email)}
                    image={person.profile_image_url}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      {personName(person.fullname, person.email)}
                    </span>
                    {person.email ? (
                      <span className="block truncate text-xs text-muted-foreground">{person.email}</span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={`Remove ${personName(person.fullname, person.email)}`}
                    onClick={() =>
                      setPeople((current) => current.filter((item) => item.user_id !== person.user_id))
                    }
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))
            )}
          </ul>

          <p className="flex gap-2 rounded-xl bg-muted/60 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            People you share with can view and turn on this skill. They can&apos;t edit it.
          </p>

          {canPublishToggle ? (
            <>
              <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                or
                <span className="h-px flex-1 bg-border" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Publish to whole organization</p>
                  <p className="text-xs text-muted-foreground">
                    Everyone in your organization can see this skill.
                  </p>
                </div>
                <Switch
                  checked={skill.visibility === "TEAM"}
                  disabled={
                    skill.visibility === "TEAM"
                      ? !skill.permissions.can_unpublish
                      : !skill.permissions.can_publish
                  }
                  aria-label="Publish to whole organization"
                  onCheckedChange={(checked) => {
                    if (checked && skill.permissions.can_publish) onPublishRequest();
                    if (!checked && skill.permissions.can_unpublish) onUnpublishRequest();
                  }}
                />
              </div>
            </>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={shares.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={shares.isPending} onClick={save}>
            {shares.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonAvatar({ name, image }: { name: string; image: string | null }) {
  return (
    <Avatar className={cn("size-8")}>
      {image ? <AvatarImage src={image} alt="" /> : null}
      <AvatarFallback className="text-[10px]">{getInitials(name) || "?"}</AvatarFallback>
    </Avatar>
  );
}
